import { detectIncognito } from 'detectincognitojs'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { WriterSection } from 'src/components/Author/WriterSection'
import { ContentWritersByline } from 'src/components/ContentWritersByline/ContentWritersByline'
// import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
// import MainContainer from 'src/components/global/MainContainer/MainContainer'
// import { PremiumModal } from 'src/components/global/PremiumModal'
// import { LegaltechJobsPanel } from 'src/components/LegaltechJobsPanel/LegaltechJobsPanel'
import { MaterialSection } from 'src/components/Material/MaterialSection'
import { RequestVendorCheatsheetButton } from 'src/components/RequestVendorCheatsheetButton/RequestVendorCheatsheetButton'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID, SHOW_FREE_ARTICLE } from 'src/config/config'
import {
  IContentBySlugQueryVariables,
  IContentEntityBySlug,
  IEnum_Content_Contenttype,
  useContentBySlugLazyQuery,
  useVisitContentMutation,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
// import { Meta } from 'src/layout/Meta'
import { AppState } from 'src/store/types'
// import { Main } from 'src/templates/Main'
import { formatNumericDisplayDate } from 'src/utils/formatNumericDisplayDate'
import {
  extractKeywordsFromQuery,
  highlightKeywordsInHtml,
  shouldHighlightKeywords,
} from 'src/utils/keywordHighlighter'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'

// import { removeTags } from 'src/utils/removeTags'
import { ArticleCart } from '../ArticleCart/ArticleCart'
import { getContentDate } from '../ContentTimestamp/ContentTimestamp'
import { getFingerprint } from '../CustomHooks/useFingerprint'
// import { ThemeOnly } from '../global/ThemeOnly'
import FreeLimitPopup from '../Popup/FreeLimitPopup'

type Props = {
  slug: string
  content: any | IContentEntityBySlug
  origin: string
  relatedContents?: IContentEntityBySlug[]
}

// const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
//   loading: () => <p className="loading-placeholder"></p>,
// })

const PremiumModal = dynamic(() => import('src/components/global/PremiumModal').then((mod) => mod.PremiumModal), {
  loading: () => <p className="loading-placeholder"></p>,
})

export const ContentPageNoHeaderFooter: NextPage<Props> = ({ content, slug, origin, relatedContents = [] }) => {
  const relatedContentsArray = relatedContents || []

  const authInfo = useSelector((state: AppState) => state.authInfo)
  const [visitContent] = useVisitContentMutation()
  const [isFreeArticle, setIsFreeArticle] = useState<boolean | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [refetch, { data }] = useContentBySlugLazyQuery()
  const router = useRouter()
  const [isNonLogin, setIsNonLogin] = useState(false)
  const [lastFreeArticle, setLastFreeArticle] = useState(false)

  const [includeInKnowHow, setIncludeInKnowHow] = useState<boolean | null>(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (localStorage.getItem(`sso`)) {
        const ssoAuthUser = localStorage.getItem(`sso`)
        setIsNonLogin(!ssoAuthUser)
      } else {
        const lastAuthUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
        )
        setIsNonLogin(!lastAuthUser)
      }
    }
  }, [])

  // Scroll to top when slug changes (when navigating to a new content page)
  useEffect(() => {
    const scrollToTop = () => {
      const mainContainer = document.getElementById('mainContainer')
      if (mainContainer) {
        mainContainer.scrollTo({ top: 0, behavior: 'auto' })
      } else {
        // Fallback to window scroll if mainContainer is not found
        window.scrollTo({ top: 0, behavior: 'auto' })
      }
    }

    // Scroll immediately and also after route change completes
    scrollToTop()

    const handleRouteChangeComplete = () => {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToTop, 100)
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [slug, router])

  if (
    data?.contentBySlug?.isUserVendor &&
    !data?.contentBySlug?.isInvited &&
    !data?.contentBySlug?.vendorSelfContent &&
    (data?.contentBySlug?.content?.attributes?.contentType === IEnum_Content_Contenttype.VendorAnalysis ||
      data?.contentBySlug?.content?.attributes?.contentType === IEnum_Content_Contenttype.CompetitiveAnalysis)
  ) {
    router.push('/restricted-page')
  }

  const title = content?.content?.attributes?.title || data?.contentBySlug?.content?.attributes?.title || ''
  const writersList = normalizeWritersList(
    content?.content?.attributes?.writer ?? data?.contentBySlug?.content?.attributes?.writer,
  )
  const snippet = content?.content?.attributes?.snippet || data?.contentBySlug?.content?.attributes?.snippet || ''
  const isLatestBlog =
    content?.content?.attributes?.isLatestBlog || data?.contentBySlug?.content?.attributes?.isLatestBlog || false

  // Extract keywords from query parameters for highlighting
  const keywords = extractKeywordsFromQuery(router.query)
  const shouldHighlight = shouldHighlightKeywords(router.query)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const contentDate = content?.content ? getContentDate(content.content as any) : null
  const defaultImageUrl =
    data?.contentBySlug?.content?.attributes?.defaultImage?.[0]?.url ||
    content?.content?.attributes?.defaultImage?.[0]?.url ||
    null
  const imageUrl = useDefaultLogo(defaultImageUrl)
  const showImage = defaultImageUrl !== null
  const publishedTime = formatNumericDisplayDate(
    content?.content?.attributes?.publishedTime || data?.contentBySlug?.content?.attributes?.publishedTime,
  )

  let { isPremium, isVendor } = useAuthenticated()

  const isContentPremium = !!content?.content?.attributes?.isPremium
  const isVendorCheatsheet = content?.content?.attributes?.blogTaxonomies?.data?.some(
    (item: any) => item.attributes?.slug === 'the-vendor-cheatsheet',
  )
  const isShowingVendorData = !isVendorCheatsheet || isVendor || isLatestBlog
  const [isInvited, setIsInvited] = useState<boolean>(false)
  const [vendorSelfContent, setVendorSelfContent] = useState<boolean>(false)
  const [isShowingPremiumContent, setIsShowingPremiumContent] = useState<boolean>(
    ((!isContentPremium || isLatestBlog || isPremium || isInvited) && isShowingVendorData) || false,
  )
  const [fullcontent, setFullContent] = useState<IContentEntityBySlug>(
    (data?.contentBySlug as IContentEntityBySlug) || content,
  )

  // Apply keyword highlighting to content if keywords are present
  // Use full content if available, otherwise use snippet
  const contentToHighlight = fullcontent?.content?.attributes?.snippet || snippet
  let displayContent = contentToHighlight
  let displayTitle = title

  // Always highlight content if keywords are present (existing functionality - must not break)
  if (shouldHighlight && keywords.length > 0) {
    displayContent = highlightKeywordsInHtml(contentToHighlight, keywords)
    // Additionally highlight title if it exists (new functionality)
    if (title && typeof title === 'string' && title.trim()) {
      displayTitle = highlightKeywordsInHtml(title, keywords)
    }
  }

  const [counted, setCounted] = useState<boolean>(false)

  let parentRoute = {
    label: 'Legaltech Hub contents',
    route: '/contents/',
  }
  if (content?.content?.attributes?.contentType === IEnum_Content_Contenttype.Blog) {
    const blogTaxonomies = content?.content?.attributes?.blogTaxonomies || []
    if (blogTaxonomies.length > 0) {
      parentRoute = {
        label: blogTaxonomies[0]?.name || 'Legaltech Hub contents',
        route: `/blogs/${blogTaxonomies[0]?.slug}`,
      }
    }
  } else if (content?.content?.attributes?.contentType === IEnum_Content_Contenttype.IndustryAnalysis) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    parentRoute = {
      label: 'Industry Analysis',
      route: `/industry-analysis`,
    }
  }

  useEffect(() => {
    const fetchFingerprintAndRefetch = async () => {
      let fingerprint = null
      let browser = { browserName: '', isPrivate: false }
      let isNonLogin = false
      // Call getFingerprint() only if SHOW_FREE_ARTICLE is true
      if (SHOW_FREE_ARTICLE) {
        fingerprint = await getFingerprint()
        browser = await detectIncognito()
        if (typeof window !== 'undefined') {
          if (localStorage.getItem(`sso`)) {
            const ssoAuthUser = localStorage.getItem(`sso`)
            isNonLogin = !ssoAuthUser
          } else {
            const lastAuthUser = localStorage.getItem(
              `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
            )
            isNonLogin = !lastAuthUser
          }
        }
      }
      if (
        (authInfo.idToken &&
          authInfo.idToken !== null &&
          authInfo.idToken !== '' &&
          authInfo.id &&
          authInfo.id !== null &&
          authInfo.id !== '') ||
        (isNonLogin && fingerprint && fingerprint != null) ||
        !SHOW_FREE_ARTICLE
      ) {
        let variable: IContentBySlugQueryVariables = { slug, accessFreeArticle: true }

        if (
          SHOW_FREE_ARTICLE &&
          fingerprint &&
          fingerprint != null &&
          !browser?.isPrivate &&
          browser?.browserName != ''
        ) {
          variable = { slug, accessFreeArticle: true, uniqueAddress: fingerprint?.toString() }
        }
        refetch({
          variables: variable,
          context: {
            headers: {
              Authorization:
                !isNonLogin && authInfo.idToken && authInfo.idToken !== '' ? `Bearer ${authInfo.idToken}` : '',
            },
          },
        }).then((res) => {
          if (res?.data?.contentBySlug) {
            let invited = false
            let freeArticle = null
            if (res?.data?.contentBySlug?.isFreeArticle == false) {
              freeArticle = false
            } else {
              freeArticle = res?.data?.contentBySlug?.isFreeArticle || null
            }
            if (res?.data?.contentBySlug?.isFreeArticle && res?.data?.contentBySlug?.freeArticleLeft == 0) {
              setLastFreeArticle(true)
            }
            const vendorSelfContent = res?.data?.contentBySlug?.vendorSelfContent || false
            setIsFreeArticle(freeArticle)
            setIncludeInKnowHow(res?.data?.contentBySlug?.includeInKnowHow || false)
            isPremium = !!authInfo.isPremium
            isVendor = !!authInfo.isVendor
            const resourcePublishedAt = res.data?.contentBySlug?.resourcePublishedAt

            if (resourcePublishedAt) {
              const yearAgoDate = new Date()
              const publishDate = new Date(resourcePublishedAt)

              // get 1 year ago date from the current date
              yearAgoDate.setFullYear(yearAgoDate.getFullYear() - 1)
              invited = (res?.data?.contentBySlug?.isInvited && publishDate >= yearAgoDate) || false
            }
            setIsInvited(invited)
            // const premiumContent =
            //   isPremium || freeArticle || !isContentPremium || invited || vendorSelfContent || isLatestBlog || false
            // console.log(premiumContent)
            let premiumContent = false
            if (freeArticle) {
              premiumContent = true //show all content if it free limit is available
            } else {
              if (isPremium) {
                premiumContent = isPremium && (!isVendorCheatsheet || isVendor) //show all content if user is premium but show the vendor content only to premium vendor
              } else {
                //for basic user
                if (!isContentPremium) {
                  premiumContent =
                    ((invited || vendorSelfContent || isLatestBlog) && (!isVendorCheatsheet || isVendor)) || false
                } else {
                  premiumContent =
                    ((!isContentPremium || invited || vendorSelfContent || isLatestBlog) &&
                      (!isVendorCheatsheet || isVendor)) ||
                    false
                }
              }
            }
            setIsShowingPremiumContent(premiumContent)
            setFullContent(res?.data?.contentBySlug as IContentEntityBySlug)
            setVendorSelfContent(vendorSelfContent)
          }
        })
      }
    }
    fetchFingerprintAndRefetch()
  }, [authInfo, slug])

  const countData = async (cancelExcution = false) => {
    if (counted || cancelExcution) {
      return
    }

    await visitContent({ variables: { content_id: content?.content?.attributes?.documentId || '' } })
  }

  // useEffect(() => {
  //   setIsShowingPremiumContent(
  //     ((isFreeArticle || !isContentPremium || isLatestBlog || isPremium || isInvited || vendorSelfContent) &&
  //       isShowingVendorData) ||
  //       false,
  //   )
  // }, [isInvited])

  useEffect(() => {
    if (
      authInfo.idToken &&
      authInfo.id &&
      !counted &&
      data?.contentBySlug?.vendorSelfContent == false &&
      data?.contentBySlug?.isFreeArticle == null
    ) {
      countData()
      setCounted(true)
    }
  }, [fullcontent])

  // useEffect(() => {
  //   if (data?.contentBySlug?.vendorSelfContent) {
  //     setVendorSelfContent(data?.contentBySlug?.vendorSelfContent)
  //   }
  // }, [data?.contentBySlug?.vendorSelfContent])

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleOpenModal = () => {
    setShowModal(true)
  }

  // const freeArticleWithallButton = (
  //   <>
  //     <FreeLimitPopup
  //       heading={`You’ve viewed your last free article.`}
  //       title={
  //         <>
  //           Create an account to access more articles or sign up for <span className="text-navybright">premium</span> to
  //           access unlimited articles.
  //         </>
  //       }
  //       createAccountButton={true}
  //       premiumSignupButton={true}
  //     />
  //   </>
  // )

  const freeArticleWithpremiumButton = (
    <FreeLimitPopup
      heading={``}
      title={
        <>
          Signup for <span className="text-navybright">premium</span> to access this article.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
    />
  )

  const freeArticleWithpremiumButtonForAllUser = (
    <FreeLimitPopup
      heading={`You have reached your free article limit for the month.`}
      title={
        <>
          Sign up for an LTH <span className="text-navybright">Premium</span> subscription to access unlimited articles.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
    />
  )

  const lastFreeArticleWithpremiumButton = (
    <>
      <FreeLimitPopup
        heading={`This is your last free article this month.`}
        title={
          <>
            To access unlimited articles, sign up for LTH <span className="text-navybright">Premium</span>.
          </>
        }
        createAccountButton={false}
        premiumSignupButton={true}
        cssModified={true}
      />
    </>
  )

  return (
    <div className="w-full items-center justify-between">
      <div className="!max-w-[1100px] flex flex-col w-full m-auto bg-white">
        <div className="flex flex-col">
          <PremiumModal origin={origin} isOpen={showModal} onClose={handleCloseModal} />
          <div className=" ">
            <div className="w-full items-center justify-between">
              <div
                className={`!max-w-[1100px] flex flex-col w-full m-auto bg-white  ${!isFreeArticle && isFreeArticle != null ? 'box-border-shadow-closing' : ''}`}
              >
                {/* <div className="w-full h-[0.4rem] bg-lightRoyalBlue"></div> */}
                <div
                  className={`tdBody flex flex-col md:pl-[4.0625rem] md:pr-[4.0625rem] pl-[20px] pr-[20px] ${!isFreeArticle && isFreeArticle != null ? 'tdBody-bottom-closing' : ''}`}
                >
                  <h1
                    className="pt-[0.1rem] pb-[0.125rem] font-bold font-secondary text-darkgray text-[1.875rem] mt-[2rem]"
                    dangerouslySetInnerHTML={{ __html: displayTitle }}
                  />
                  <div className="print-flex flex flex-row mb-[2.2rem] mt-[1rem] items-center flex-wrap">
                    <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
                      Published on {publishedTime}
                    </span>
                    {writersList.length > 0 && (
                      <>
                        <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
                          &nbsp;by
                        </span>
                        <ContentWritersByline writers={writersList} variant="detail" />
                      </>
                    )}
                  </div>
                  <div className="divide-x h-[1px] bg-mist mb-[1.6rem]"></div>
                  {isShowingPremiumContent &&
                    !['blog', 'industry_analysis', 'vendor_resource', 'thought_leadership', 'buyers_guide'].includes(
                      fullcontent?.content?.attributes?.contentType?.toLowerCase() || '',
                    ) &&
                    fullcontent?.content?.attributes?.downloadMaterial && (
                      <div className="blog-content html-show mb-[2rem] py-[1rem]">
                        {isShowingPremiumContent && (
                          <div className="materialSectionPrint md:float-right md:ml-[40px] mt-10 md:mt-0 md:mb-4 max-w-[500px] min-w-[465px] max-h-[500px] min-h-[496px]">
                            <MaterialSection
                              isInvited={isInvited}
                              contentId={content.content?.attributes?.documentId || ''}
                              downloadMaterial={fullcontent.content.attributes?.downloadMaterial}
                              isContentPremium={isContentPremium}
                              onDisabledDownloadClick={handleOpenModal}
                              vendorSelfContent={vendorSelfContent}
                              isFreeArticle={isFreeArticle}
                            />
                          </div>
                        )}
                        <div className="html-show">
                          <HtmlContentViewer
                            className="blog-content html-show font-secondary font-semibold text-darkgray text-[22px] leading-[32px]"
                            html={displayContent}
                          />
                        </div>
                      </div>
                    )}
                  {isShowingPremiumContent && (
                    <HtmlContentViewer
                      className="blog-content html-show mb-[3rem] w-full overflow-auto"
                      html={displayContent}
                      image={
                        !fullcontent?.content?.attributes?.downloadMaterial && imageUrl && showImage ? (
                          <Image
                            className="w-full bg-gray object-contain	"
                            alt="logo"
                            src={imageUrl}
                            width={1000}
                            height={1000}
                            priority
                          />
                        ) : null
                      }
                      download={
                        fullcontent?.content?.attributes?.downloadMaterial &&
                        ![
                          'business_case',
                          'competitive_analysis',
                          'evaluation_framework',
                          'knowhow',
                          'landscape_article',
                          'legaltech_resources_for_lawyers',
                          'pilot_guide',
                          'premium_resource',
                          'requirements_template',
                          'vendor_analysis',
                          'vendor_content',
                        ].includes(fullcontent?.content?.attributes?.contentType?.toLowerCase() || '') ? (
                          <div className="mb-10">
                            <MaterialSection
                              isInvited={isInvited}
                              contentId={content.content?.attributes?.documentId || ''}
                              downloadMaterial={fullcontent.content.attributes?.downloadMaterial}
                              isContentPremium={isContentPremium}
                              onDisabledDownloadClick={handleOpenModal}
                              vendorSelfContent={vendorSelfContent}
                              isFreeArticle={isFreeArticle}
                            />
                          </div>
                        ) : null
                      }
                    />
                  )}
                  {!isShowingPremiumContent && (
                    <HtmlContentViewer
                      className="blog-content html-show mb-[3rem] w-full overflow-auto"
                      html={displayContent}
                      image={
                        imageUrl && showImage ? (
                          <Image
                            className="w-full bg-gray object-contain	"
                            alt="logo"
                            src={imageUrl}
                            width={1000}
                            height={1000}
                            priority
                          />
                        ) : null
                      }
                    />
                  )}
                  {/* this is for basic user/premium user and includeinknowhow is false */}
                  {!isShowingPremiumContent && !isNonLogin && (
                    <div className="flex justify-center">
                      {/* this is for premium user only */}
                      {isVendorCheatsheet && isPremium && <RequestVendorCheatsheetButton />}
                      {/* this is for basic user and includeinknowhow is false */}
                      {!isPremium && isFreeArticle == null && freeArticleWithpremiumButton}
                    </div>
                  )}
                  {/* this is for not login user and includeinknowhow is false */}
                  {isNonLogin && isFreeArticle == null && freeArticleWithpremiumButton}
                  {/* this is for not login user/basic user and includeinknowhow is true */}
                  {!isFreeArticle && isFreeArticle != null && freeArticleWithpremiumButtonForAllUser}
                  {/* this is for not login/basic user and includeinknowhow is true and last free article is true */}
                  {isFreeArticle && lastFreeArticle && lastFreeArticleWithpremiumButton}

                  {(isFreeArticle === true || (isPremium && includeInKnowHow === false)) &&
                    relatedContentsArray &&
                    relatedContentsArray.length > 0 && <ArticleCart articles={relatedContentsArray} from="contents" />}
                  {isFreeArticle && writersList.map((w, i) => <WriterSection key={i} writer={w} />)}
                </div>
              </div>
              {/* <div className="w-full bg-clouds py-10">
                <LegaltechJobsPanel className="searchLegaltechJobs !max-w-[1100px]" />
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
